import type { Translation } from '../i18n-types'

const de_settings = {
  settingsPage: {
    headline: 'Einstellungen',
    methodology: {
      checkinDueFrequencies: {
        biweekly: 'Zweiwöchentlich (empfohlen)',
        headline: 'Check-in Fälligkeitsfrequenzen',
        intro: 'Wählen Sie aus, nach welcher Frequenz Check-ins fällig sind',
        monthly: 'Monatlich',
        off: 'Nie',
        weekly: 'Wöchentlich',
      },
      errors: {
        updateFailed: 'Fehler beim Aktualisieren der Methodikeinstellungen',
      },
      headline: 'Methodik',
      intro:
        'Passen Sie an, wie Sie die AOA-Methodik in Ihrer Organisation anwenden. Diese Einstellungen gelten für alle Mitglieder der Organisation.',
      iterationCycles: {
        cycleStartDateDescription:
          'Sie können bei Bedarf den Start der Iterationen ändern, beispielsweise wenn Ihr Geschäftsjahr nicht am 1. Januar beginnt.',
        cycleStartDateLabel: 'Jährlicher Iterationsstart',
        cycleStartDatePlaceholder: 'Standard (1. Januar)',
        earlyTermination:
          'Diese Änderung führt dazu, dass die aktuelle Iteration am {0} vorzeitig endet.',
        headline: 'Iterationszyklen',
        intro:
          'Legen Sie fest, wann Ihre Iterationszyklen beginnen und enden. Dies hat keine Auswirkungen auf vergangene Iterationen.',
        iterationNamePlaceholder: 'Standard',
        legacyCycle: 'Alter Zyklus',
        monthIteration: '{year} - Monatliche Iteration {month}',
        monthIterationNameLabel: 'Monatlicher Iterationsname',
        quarterIterationNameLabel: 'Quartals-Iterationsname',
        quarterIteration: '{year} - Quartals-Iteration {quarter}',
        yearlyIteration: 'Jährliche Iteration {0}',
        yearlyIterationNameLabel: 'Jährlicher Iterationsname',
      },
      updateSuccess: 'Methodikeinstellungen aktualisiert',
      i18nSettings: {
        headline: 'Methodologie-Terminologie',
        intro:
          'Passen Sie die Terminologie an, die in der AOA-Methodik verwendet wird. Diese Einstellungen gelten für alle Mitglieder der Organisation.',
        terms: {
          actionHeadline: 'Action',
          action: 'Action',
          actionPlural: 'Actions',
          actionDescription:
            'Eine konkrete Aktivität mit klarer Ergebniserwartung, die direkt zur Erfüllung des übergeordneten Intents beiträgt.',
          intentHeadline: 'Intent',
          intent: 'Intent',
          intentPlural: 'Intents',
          intentDescription:
            'Eine Maßnahme, die das im übergeordneten Intent definierte Handlungsfeld konkretisiert.',
          higherIntentHeadline: 'Higher Intent',
          higherIntent: 'Higher Intent',
          higherIntentPlural: 'Higher Intents',
          higherIntentDescription:
            'Eine langfristige strategische Stoßrichtung, die den Fokus und die Entwicklungsprioritäten vorgibt und die Grundlage für die Ableitung aller nachgelagerten Intents und Actions bildet.',
          deliverableHeadline: 'Deliverable',
          deliverable: 'Deliverable',
          deliverablePlural: 'Deliverables',
          deliverableDescription:
            'Deliverables sind optional definierte Teilkomponenten eines Action Achievements, die Check-ins und Fortschrittsmessung erleichtern, insbesondere, wenn das Action Achievement quantitative Aspekte enthält.',
          targetHeadline: 'Target',
          target: 'Target',
          targetPlural: 'Targets',
          targetDescription:
            'Eine qualitativ oder quantitativ messbare Zielgröße, die den angestrebten Erfolg innerhalb eines definierten Zeitraums beschreibt und als Wegweiser für die Ausgestaltung nachgelagerter Intents und Actions dient.',
          actionAchievementHeadline: 'Action Achievement',
          actionAchievement: 'Action Achievement',
          actionAchievementPlural: 'Action Achievements',
          actionAchievementDescription:
            'Eine klar formulierte qualitative oder quantitative Ergebniserwartung, die die erfolgreiche Erfüllung einer Action definiert.',
        },
        typeHere: 'Hier tippen...',
        removeCustomTerm: 'Benutzerdefinierten Begriff entfernen',
        addCustomTerm: 'Anpassen',
        pluralForm: 'Pluralform',
      },
    },
    notifications: {
      checkins: {
        frequencies: {
          biweekly: 'Zweiwöchentlich',
          inherited: 'Standard',
          inheritedDescription: 'Standard der Organisation verwenden',
          monthly: 'Monatlich',
          off: 'Benachrichtigungen deaktivieren',
          weekly: 'Wöchentlich',
        },
        headline: 'Check-In Erinnerungen',
        intro:
          'Erhalten Sie eine Erinnerung zum Check-In, wenn Sie dies nicht getan haben',
      },
      headline: 'Benachrichtigungen',
      subheader: 'Passen Sie an, wie Sie Benachrichtigungen erhalten',
    },
    organization: {
      communication: {
        checkinReminderEmails: 'Check-in-Erinnerungs-E-Mails',
        defaultReminderInterval: 'Standard-Erinnerungsintervall',
        defaultReminderIntervalDescription:
          'Das Standard-Erinnerungsintervall für Mitglieder der Organisation',
        failedToUpdate:
          'Fehler beim Aktualisieren der Kommunikationseinstellungen',
        headline: 'Kommunikation',
        intro:
          'Aktualisieren Sie Ihre Kommunikationseinstellungen für die Organisation',
        reminderEmailsDescription:
          'Mitglieder der Organisation an fehlende Check-ins per E-Mail erinnern',
        sendMonthlyAtEnd: 'Zum Monatsende',
        sendMonthlyAtStart: 'Zum Monatsanfang',
        timeOfDay: 'Tageszeit, zu der Erinnerungs-E-Mails gesendet werden',
        timeOfDayDescription:
          'Die Zeit wird in Ihrer lokalen Zeitzone angegeben',
        updated: 'Kommunikationseinstellungen aktualisiert',
        weekDay: 'Wochentag',
        weekDayDescription:
          'Der Wochentag, an dem Erinnerungs-E-Mails gesendet werden',
      },
      displayName: {
        displayName: 'Anzeigename',
        failedToUpdate: 'Fehler beim Aktualisieren des Firmennamens',
        headline: 'Organisationsname',
        intro: 'Wie Ihre Organisation in der App angezeigt wird',
        updated: 'Organisationsname aktualisiert',
      },
      domain: {
        addAdditionalDomain: 'Weitere Domain hinzufügen',
        createNewDomain: 'Neue Domain erstellen',
        createNewDomainDescription:
          'Erlauben Sie den Mitgliedern Ihrer Organisation, sich mit einer eindeutigen URL anzumelden.',
        domainLabel: 'Domain',
        headline: 'Anmeldedomain der Organisation',
        intro: 'Eine eindeutige Anmelde-URL für Ihre Organisation',
        limitReached:
          'Sie haben das Limit an Domains erreicht, die Sie erstellen können',
        noDomainsCreatedDescription: 'Sie haben noch keine Domains erstellt',
        noDomainsCreatedTitle: 'Keine Domains erstellt',
        subdomainAlreadyExists: 'Diese Subdomain existiert bereits',
      },
      msTeamsConnections: {
        headline: 'Microsoft Teams Integration',
        intro: 'Verbinden Sie Ihre Organisation mit Microsoft Teams',
        noTeamsConnectionsCreatedDescription:
          'Sie haben noch keine Verbindungen zu Microsoft Teams eingerichtet.',
        noTeamsConnectionsCreatedTitle: 'Keine Teams-Integration eingerichtet',
        commentLabel: 'Bemerkung',
        downloadApp: 'App herunterladen',
        addAppToTeamsHeader: 'GrowthSquare zu Microsoft Teams hinzufügen',
        manageConnectionHeader: 'Microsoft Teams-Verbindung verwalten',
        authenticateTeamsHeader: 'Zugriff von Microsoft Teams freigeben',
        introDescription:
          'Erlauben Sie den Mitgliedern Ihrer Organisation, mit der Strategie Ihrer Organisation über einen KI-gestützten Chatbot in Microsoft Teams zu interagieren.',
      },
      headline: 'Organisation',
      logo: {
        failedToUpdate: 'Fehler beim Aktualisieren des Firmenlogos',
        headline: 'Firmenlogo',
        intro:
          'Laden Sie ein Logo für Ihre Organisation hoch, das in der App angezeigt wird',
      },
      sso: {
        headline: 'Single sign-on',
        initialRolesLabel: 'Initiale Rolle',
        intro:
          'Erlauben Sie den Mitgliedern Ihrer Organisation, sich mit ihrem zentralen Login anzumelden.',
        noSsoConfiguredDescription:
          'SSO ist noch nicht für Ihre Organisation eingerichtet',
        noSsoConfiguredTitle: 'Kein SSO konfiguriert',
        providerLabel: 'Provider',
        scim: {
          baseUrl: 'Endpunkt Basis-URL',
          configureScimHeader: 'Verzeichnissynchronisierung konfigurieren',
          configureScimIntro:
            'Verwenden Sie SCIM, um Benutzer von Ihrem IdP zu synchronisieren. Dadurch können Sie Benutzer auswählen, die die Plattform noch nicht verwenden.',
          configureScimMenuItem: 'Verzeichnissynchronisierung verwalten',
          enabledAt: 'Eingerichtet am {0}',
          renewConfirm:
            'Sind Sie sicher, dass Sie das Token erneuern möchten? Dies macht das alte Token ungültig und Sie müssen es in Ihrem IdP aktualisieren.',
          renewToken: 'Token erneuern',
          scimNotConfiguredDescription:
            'Konfigurieren Sie einen SCIM v2-Endpunkt, um Benutzer von Ihrem IdP zu synchronisieren.',
          scimNotConfiguredTitle: 'SCIM nicht konfiguriert',
          secretToken: 'Authentifizierungs-Token',
          setupScimCta: 'SCIM einrichten',
        },
      },
      subheader: 'Aktualisieren Sie hier Ihre Organisationsinformationen',
      team: {
        addAnother: 'Weitere hinzufügen',
        addToTeamModal: {
          description:
            'Fügen Sie diesen Benutzer als direktes Mitglied einem der verbleibenden Teams hinzu. Teams, denen der Benutzer bereits angehört, werden nicht angezeigt.',
          title: 'Benutzer zum Team hinzufügen',
        },
        headline: 'Team-Management',
        inviteTeamMembersHeader: 'Teammitglieder einladen',
        inviteTeamMembersIntro:
          'Arbeiten Sie mit Ihren Teammitgliedern an dem Erfolg Ihrer Organisation.',
        removeFromTeamModal: {
          description:
            'Dieser Benutzer wird aus dem Team entfernt, beachten Sie, dass der Benutzer immer noch seinen Ergebnissen und Aktionen zugeordnet wird.',
          title: 'Sind Sie sicher?',
        },
        sendInvites: 'Einladungen versenden',
        subheader:
          'Verwalten Sie hier Ihre Teammitglieder und ihre Kontoberechtigungen.',
      },
    },
    personal: {
      displayName: {
        headline: 'Ihr Name',
        intro: 'Wie Ihr Name in der App angezeigt wird',
      },
      externalManaged: {
        description:
          'Kontaktieren Sie Ihren Administrator, um Ihr Profil zu aktualisieren',
        headline: 'Ihr Profil wird von Ihrer Organisation verwaltet',
      },
      headline: 'Konto',
      language: 'Sprache',
      languageDescription: 'Passen Sie an, wie die App für Sie angezeigt wird.',
      picture: {
        failedToUpdate: 'Profilbild konnte nicht aktualisiert werden',
        headline: 'Profilbild',
        intro: 'Laden Sie ein Profilbild hoch',
      },
      profile: {
        department: 'Abteilung',
        departmentDescription: 'Die Abteilung, in der Sie arbeiten',
        displayName: 'Vollständiger Name',
        email: 'E-Mail-Adresse',
        failedToUpdate:
          'Persönliche Informationen konnten nicht aktualisiert werden',
        firstName: 'Vorname',
        headline: 'Persönliche Informationen',
        intro: 'Aktualisieren Sie Ihre persönlichen Informationen',
        lastName: 'Nachname',
        phoneNumber: 'Telefonnummer',
        updated: 'Persönliche Informationen aktualisiert',
      },
      subheader:
        'Aktualisieren Sie hier Ihre persönlichen Informationen und Einstellungen',
    },
    subheader: 'Passen Sie Ihr Erlebnis an',
  },
} satisfies Pick<Translation, 'settingsPage'>

export default de_settings
